import React, { useState } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled(motion.div)`
  background: white;
  border-radius: 20px;
  padding: 40px;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;

  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #666;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;

  &:hover {
    background: #f0f0f0;
    color: #000;
  }
`;

const ModalTitle = styled.h2`
  font-size: 28px;
  margin-bottom: 10px;
  color: #333;
  font-weight: bold;
`;

const ModalSubtitle = styled.p`
  color: #666;
  margin-bottom: 30px;
  font-size: 14px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-weight: 600;
  color: #333;
  font-size: 14px;
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 2px solid #e0e0e0;
  border-radius: 10px;
  font-size: 14px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #ff6b00;
  }

  &::placeholder {
    color: #aaa;
  }
`;

const TextArea = styled.textarea`
  padding: 12px 16px;
  border: 2px solid #e0e0e0;
  border-radius: 10px;
  font-size: 14px;
  resize: vertical;
  min-height: 100px;
  font-family: inherit;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #ff6b00;
  }

  &::placeholder {
    color: #aaa;
  }
`;

const SubmitButton = styled.button`
  padding: 14px 24px;
  background: #ff6b00;
  color: white;
  border: none;
  border-radius: 50px;
  font-weight: bold;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.2s;
  margin-top: 10px;

  &:hover {
    background: #e55f00;
    transform: scale(1.02);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: scale(1);
  }
`;

const SuccessMessage = styled.div`
  text-align: center;
  padding: 40px 20px;

  h3 {
    color: #28a745;
    font-size: 24px;
    margin-bottom: 10px;
  }

  p {
    color: #666;
    margin-bottom: 20px;
  }
`;

const ErrorMessage = styled.div`
  background: #fee;
  color: #c33;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 20px;
  border: 1px solid #fcc;
`;

const BookingModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    quantity: "",
    address: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");

    try {
      const leadData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        quantity: parseFloat(formData.quantity),
        message: `Address: ${formData.address}\n\nAdditional Notes: ${formData.message}`,
        source: "website",
      };

      const response = await fetch("https://api.dieselonsite.in/api/leads", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(leadData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to submit request");
      }

      setIsSubmitting(false);
      setIsSuccess(true);

      // Reset form and close modal after 3 seconds
      setTimeout(() => {
        setIsSuccess(false);
        setFormData({
          name: "",
          phone: "",
          email: "",
          quantity: "",
          address: "",
          message: "",
        });
        onClose();
      }, 3000);
    } catch (err) {
      setIsSubmitting(false);
      setError(err.message || "Failed to submit request. Please try again.");
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleOverlayClick}
        >
          <ModalContent
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            <CloseButton onClick={onClose}>&times;</CloseButton>

            {isSuccess ? (
              <SuccessMessage>
                <h3>✓ Request Submitted!</h3>
                <p>
                  We'll contact you shortly to confirm your diesel delivery.
                </p>
              </SuccessMessage>
            ) : (
              <>
                <ModalTitle>Diesel on Call Booking Form</ModalTitle>
                <ModalSubtitle>
                  Fill in the details below and we'll get back to you within 2
                  hours.
                </ModalSubtitle>

                {error && <ErrorMessage>{error}</ErrorMessage>}

                <Form onSubmit={handleSubmit}>
                  <FormGroup>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      type="text"
                      id="name"
                      name="name"
                      placeholder="Enter your name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      type="tel"
                      id="phone"
                      name="phone"
                      placeholder="Enter your phone number"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      type="email"
                      id="email"
                      name="email"
                      placeholder="Enter your email"
                      value={formData.email}
                      onChange={handleChange}
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="quantity">Quantity (Liters) *</Label>
                    <Input
                      type="number"
                      id="quantity"
                      name="quantity"
                      placeholder="Enter quantity in liters"
                      value={formData.quantity}
                      onChange={handleChange}
                      min="1"
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="address">Delivery Address *</Label>
                    <TextArea
                      id="address"
                      name="address"
                      placeholder="Enter complete delivery address"
                      value={formData.address}
                      onChange={handleChange}
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="message">Additional Notes</Label>
                    <TextArea
                      id="message"
                      name="message"
                      placeholder="Any special instructions or requirements"
                      value={formData.message}
                      onChange={handleChange}
                      style={{ minHeight: "80px" }}
                    />
                  </FormGroup>

                  <SubmitButton type="submit" disabled={isSubmitting}>
                    {isSubmitting ? "Submitting..." : "Submit Request"}
                  </SubmitButton>
                </Form>
              </>
            )}
          </ModalContent>
        </ModalOverlay>
      )}
    </AnimatePresence>
  );
};

export default BookingModal;
